import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import { toast } from "sonner";
import {
    LoaderCircle, Save, Search, CheckSquare, Square,
    Building2, BedDouble, UtensilsCrossed, Waves, Sparkles, Music,
    Baby, Briefcase, Car, Plus, Check, RotateCcw,
} from "lucide-react";
import { AMENITY_CATEGORIES, flattenAmenities, bucketLegacyAmenities } from "@/lib/amenitiesCatalog";

const ICONS = { Building2, BedDouble, UtensilsCrossed, Waves, Sparkles, Music, Baby, Briefcase, Car, Plus };

export default function HotelAmenitiesPage() {
    const { user } = useAuth() || {};
    const hotelId = user?.hotel_id;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [activeCat, setActiveCat] = useState(AMENITY_CATEGORIES[0].key);
    const [selected, setSelected] = useState({});       // { catKey: [labels] }
    const [originalSnapshot, setOriginalSnapshot] = useState({});

    useEffect(() => {
        if (!hotelId) return;
        setLoading(true);
        api.get(`/admin/hotels/${hotelId}`).then((r) => {
            const h = r.data || {};
            let cat = h.amenities_categorized;
            // Fallback: bucket legacy flat list into categories on first save
            if (!cat || Object.keys(cat).length === 0) {
                cat = bucketLegacyAmenities(h.amenities || []);
            } else {
                // Ensure every defined category key exists in state (even empty)
                AMENITY_CATEGORIES.forEach((c) => { if (!cat[c.key]) cat[c.key] = []; });
            }
            setSelected(cat);
            setOriginalSnapshot(JSON.parse(JSON.stringify(cat)));
        }).finally(() => setLoading(false));
    }, [hotelId]);

    const toggle = (catKey, item) => {
        setSelected((prev) => {
            const cur = prev[catKey] || [];
            const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
            return { ...prev, [catKey]: next };
        });
    };

    const toggleAllInCat = (catKey, allItems) => {
        setSelected((prev) => {
            const cur = prev[catKey] || [];
            const allOn = allItems.every((it) => cur.includes(it));
            return { ...prev, [catKey]: allOn ? [] : [...allItems] };
        });
    };

    const handleSave = async () => {
        if (!hotelId) return;
        setSaving(true);
        try {
            // Persist both: legacy flat `amenities` (for backward-compat search/filter)
            // AND structured `amenities_categorized`
            const flat = flattenAmenities(selected);
            await api.patch(`/admin/hotels/${hotelId}`, {
                amenities: flat,
                amenities_categorized: selected,
            });
            setOriginalSnapshot(JSON.parse(JSON.stringify(selected)));
            toast.success("Tesis özellikleri kaydedildi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydetme başarısız");
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setSelected(JSON.parse(JSON.stringify(originalSnapshot)));
        toast.info("Değişiklikler geri alındı");
    };

    const totalSelected = useMemo(() => flattenAmenities(selected).length, [selected]);
    const hasChanges = useMemo(
        () => JSON.stringify(selected) !== JSON.stringify(originalSnapshot),
        [selected, originalSnapshot],
    );

    const activeCategory = AMENITY_CATEGORIES.find((c) => c.key === activeCat) || AMENITY_CATEGORIES[0];
    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return activeCategory.items;
        return activeCategory.items.filter((it) => it.toLowerCase().includes(q));
    }, [activeCategory, search]);

    if (loading) {
        return (
            <HotelLayout title="Tesis Özellikleri">
                <div className="flex items-center justify-center py-20">
                    <LoaderCircle className="h-10 w-10 animate-spin text-emerald-500" />
                </div>
            </HotelLayout>
        );
    }

    return (
        <HotelLayout
            title="Tesis Özellikleri"
            action={
                <div className="flex items-center gap-2">
                    <span className="px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-xs font-bold" data-testid="amenities-count">
                        {totalSelected} özellik seçili
                    </span>
                    {hasChanges && (
                        <button
                            data-testid="amenities-reset"
                            onClick={handleReset}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
                        >
                            <RotateCcw className="h-4 w-4" /> Geri Al
                        </button>
                    )}
                    <button
                        data-testid="amenities-save"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-400 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors"
                    >
                        {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                </div>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
                {/* Category sidebar */}
                <aside className="space-y-1 lg:sticky lg:top-20 lg:self-start" data-testid="amenities-cats">
                    <div className="p-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 px-2">Kategoriler</div>
                        {AMENITY_CATEGORIES.map((cat) => {
                            const Icon = ICONS[cat.icon] || Plus;
                            const count = (selected[cat.key] || []).length;
                            const isActive = activeCat === cat.key;
                            return (
                                <button
                                    key={cat.key}
                                    data-testid={`cat-${cat.key}`}
                                    onClick={() => setActiveCat(cat.key)}
                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive
                                        ? `bg-${cat.color}-500 text-white`
                                        : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        <Icon className="h-4 w-4 flex-shrink-0" />
                                        <span className="truncate">{cat.label}</span>
                                    </span>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isActive ? "bg-white/25 text-white" : `bg-${cat.color}-100 dark:bg-${cat.color}-950/40 text-${cat.color}-700 dark:text-${cat.color}-300`}`}>
                                        {count}/{cat.items.length}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {hasChanges && (
                        <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-[11px] text-amber-800 dark:text-amber-200" data-testid="amenities-dirty-banner">
                            <strong>Kaydedilmemiş değişiklik var.</strong> Sağ üstten <em>Kaydet</em>&apos;e basmayı unutmayın.
                        </div>
                    )}
                </aside>

                {/* Items grid */}
                <section className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 min-h-[400px]">
                    <header className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <div>
                            <h2 className={`text-lg font-bold text-${activeCategory.color}-600 dark:text-${activeCategory.color}-400 flex items-center gap-2`}>
                                {(() => { const Ic = ICONS[activeCategory.icon] || Plus; return <Ic className="h-5 w-5" />; })()}
                                {activeCategory.label}
                            </h2>
                            <p className="text-[11px] text-slate-500 mt-0.5">Bu otelin sunduğu özellikleri seçin · {filteredItems.length} özellik listeleniyor</p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                <input
                                    data-testid="amenities-search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Bu kategoride ara..."
                                    className="pl-8 pr-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs w-44"
                                />
                            </div>
                            <button
                                data-testid="cat-toggle-all"
                                onClick={() => toggleAllInCat(activeCategory.key, activeCategory.items)}
                                className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                            >
                                {(selected[activeCategory.key] || []).length === activeCategory.items.length ? "Hiçbirini Seçme" : "Tümünü Seç"}
                            </button>
                        </div>
                    </header>

                    {filteredItems.length === 0 ? (
                        <div className="p-12 text-center text-sm text-slate-500" data-testid="amenities-empty">
                            &quot;{search}&quot; için sonuç bulunamadı.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {filteredItems.map((item) => {
                                const checked = (selected[activeCategory.key] || []).includes(item);
                                return (
                                    <button
                                        key={item}
                                        data-testid={`amenity-${activeCategory.key}-${item.replace(/\s+/g, "-").toLowerCase()}`}
                                        onClick={() => toggle(activeCategory.key, item)}
                                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-left transition-all border ${checked
                                            ? `bg-${activeCategory.color}-50 dark:bg-${activeCategory.color}-950/30 border-${activeCategory.color}-500 text-${activeCategory.color}-800 dark:text-${activeCategory.color}-200 font-semibold shadow-sm`
                                            : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-600"}`}
                                    >
                                        {checked ? (
                                            <CheckSquare className={`h-4 w-4 text-${activeCategory.color}-600 dark:text-${activeCategory.color}-400 flex-shrink-0`} />
                                        ) : (
                                            <Square className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                        )}
                                        <span className="flex-1 min-w-0">{item}</span>
                                        {checked && <Check className={`h-3 w-3 text-${activeCategory.color}-600 dark:text-${activeCategory.color}-400 flex-shrink-0`} />}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>
            </div>

            {/* Bottom info */}
            <div className="mt-6 p-4 rounded-2xl bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-900 text-sm text-sky-900 dark:text-sky-200">
                <strong>Bilgi:</strong> Seçtiğiniz özellikler hem otel arama filtrelerinde hem de müşterilerin gördüğü otel detay sayfasında <em>kategori başlıkları altında</em> yayınlanır. Müşterilerin doğru kararı verebilmesi için lütfen mevcut olmayan özellikleri seçmeyin.
            </div>
        </HotelLayout>
    );
}
